// frontend/src/modules/esign/pages/TemplatesPage.tsx
//
// Reusable-templates list — templates carry no `original_blob_url` (stripped
// server-side, same rationale as documents; the PDF is only ever streamed
// through the authenticated `/pdf` proxy). Row click opens the editor
// (rename, edit roles + field placements). Per-row actions are
// `esign.send`-gated the same way the "New agreement" affordance is on
// DocumentsListPage: "Use" opens UseTemplateDialog preselected to this
// template, "Archive" soft-deletes it (confirm-gated).
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FileStack } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { DataTable, type DataTableColumn } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { Button } from '../../../components/ui/button';
import { formatDateTime } from '../../../design-system/format';
import { usePermission } from '../../../core/auth/usePermission';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { esignTemplatesApi } from '../api';
import { UseTemplateDialog } from '../components/UseTemplateDialog';
import type { EsignTemplate } from '../types';

export function TemplatesPage() {
  const navigate = useNavigate();
  const canView = usePermission('esign.view');
  const canSend = usePermission('esign.send');
  const confirm = useConfirm();

  const [rows, setRows] = React.useState<EsignTemplate[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [archivingId, setArchivingId] = React.useState<string | null>(null);
  const [useDialogTemplateId, setUseDialogTemplateId] = React.useState<string | null>(null);

  const load = React.useCallback(() => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    esignTemplatesApi.listTemplates({ limit: 100 })
      .then((page) => setRows(page.data))
      .catch((err: any) => setError(err?.message ?? 'Failed to load templates'))
      .finally(() => setLoading(false));
  }, [canView]);

  React.useEffect(() => { load(); }, [load]);

  if (!canView) {
    return (
      <div className="min-h-screen">
        <PageHeader eyebrow="Money · E-Sign" title="Templates" />
        <div className="px-6 sm:px-8 lg:px-10 pb-12">
          <NoAccessState description="Your role doesn't include e-sign. Ask a workspace owner if you think you should have access." />
        </div>
      </div>
    );
  }

  async function handleArchive(t: EsignTemplate, e: React.MouseEvent) {
    e.stopPropagation();
    const ok = await confirm({
      title: 'Archive this template?',
      description: `"${t.name}" will no longer be usable to create new documents. This cannot be undone.`,
      confirmLabel: 'Archive',
      destructive: true,
    });
    if (!ok) return;
    setArchivingId(t.id);
    try {
      await esignTemplatesApi.archiveTemplate(t.id);
      toast.success('Template archived');
      load();
    } catch (err: any) {
      toast.error('Failed to archive template', { description: err?.message ?? String(err) });
    } finally {
      setArchivingId(null);
    }
  }

  const columns: DataTableColumn<EsignTemplate>[] = [
    { key: 'name', header: 'Name', cell: (t) => <span className="font-medium">{t.name}</span> },
    {
      key: 'signing_order',
      header: 'Signing order',
      cell: (t) => (t.signing_order === 'sequential' ? 'In order' : 'Any order'),
    },
    { key: 'page_count', header: 'Pages', cell: (t) => t.page_count },
    { key: 'created_at', header: 'Created', cell: (t) => formatDateTime(t.created_at) },
  ];

  if (canSend) {
    columns.push({
      key: 'actions',
      header: '',
      cell: (t) => (
        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="outline" onClick={() => setUseDialogTemplateId(t.id)}>
            Use
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={archivingId === t.id}
            onClick={(e) => handleArchive(t, e)}
          >
            Archive
          </Button>
        </div>
      ),
    });
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Money · E-Sign"
        title="Templates"
        description="Reusable agreements — save time by starting from a saved layout instead of uploading fresh every time."
        actions={
          <Link to="/dashboard/esign">
            <Button variant="outline" size="default">Back to agreements</Button>
          </Link>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {error ? (
          <ErrorState title="Couldn't load templates" description={error} onRetry={load} />
        ) : (
          <DataTable<EsignTemplate>
            loading={loading}
            rows={rows}
            rowKey={(t) => t.id}
            onRowClick={(t) => navigate(`/dashboard/esign/templates/${t.id}`)}
            columns={columns}
            emptyState={
              <EmptyState
                icon={<FileStack />}
                title="No templates yet"
                description='Send an agreement, then use "Save as template" to reuse its roles and field placements.'
              />
            }
          />
        )}
      </div>

      <UseTemplateDialog
        open={useDialogTemplateId !== null}
        onOpenChange={(open) => { if (!open) setUseDialogTemplateId(null); }}
        templateId={useDialogTemplateId ?? undefined}
      />
    </div>
  );
}
